import { FC, Fragment } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import MiddleDot from '@/shared/components/typography/MiddleDot';
import { formatDate } from '@/utilities';

import SmallResourceFooter from '../SmallResourceFooter';

interface DocumentReaderFooterProps {
    available: boolean;
    date?: string;
    dateLabel?: string;
    documentType?: string;
    loading: boolean;
    publisher?: string;
    selectedText?: string;
    title?: string;
}

const DocumentReaderFooter: FC<DocumentReaderFooterProps> = ({
    available,
    date,
    dateLabel = 'Published',
    documentType,
    loading,
    publisher,
    selectedText,
    title,
}) => {
    const { token } = useAntDesignTheme();
    const parsedDate = date ? new Date(date) : undefined;
    const validDate = parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : undefined;
    const wordCount = selectedText?.trim().split(/\s+/).filter(Boolean).length ?? 0;
    const status = loading ? 'Loading document…' : !available ? 'Document unavailable' : undefined;
    const metadata = (values: Array<string | undefined>) => {
        const items = values.filter((value): value is string => Boolean(value?.trim()));
        return (
            <small
                title={items.join(' · ')}
                style={{
                    color: token.colorTextTertiary,
                    display: 'block',
                    lineHeight: token.lineHeightSM,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {items.map((item, index) => (
                    <Fragment key={`${index}:${item}`}>
                        {index ? <MiddleDot horizontalMargin='0.5rem' /> : null}
                        <span>{item}</span>
                    </Fragment>
                ))}
            </small>
        );
    };

    return (
        <SmallResourceFooter
            left={metadata([publisher, documentType])}
            center={metadata([title])}
            right={metadata([
                validDate ? `${dateLabel} ${formatDate(validDate)}` : undefined,
                status ??
                    (wordCount
                        ? `${wordCount.toLocaleString()} ${wordCount === 1 ? 'word' : 'words'} selected`
                        : undefined),
            ])}
            showHelp={false}
            style={{ columnGap: '0.75rem', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
        />
    );
};

export default DocumentReaderFooter;
